"use client";

import { CollectUser, createChat } from "@ufadiz/chat-widget";
import { type FunctionComponent, useEffect, useRef } from "react";

type ChatInstance = {
  currentSessionId?: { value: string | null };
  startNewSession?(...args: unknown[]): Promise<unknown>;
};
type VueAppConfig = {
  config?: { globalProperties?: { $chat?: ChatInstance } };
};
type VueAppContext = {
  _context?: { provides?: { ChatOptions?: { initialMessages?: string[] } } };
};
type AppRefWithCleanup = {
  __cleanup?(): void;
  unmount?(): void;
};

const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME!;
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION!;
const PLATFORM = process.env.NEXT_PUBLIC_PLATFORM!;
const WEBHOOK_URL = process.env.NEXT_PUBLIC_N8N_WEBHOOK_URL!;

const SESSION_KEY = "n8n-chat/sessionId";
const STORAGE_KEY = "neoliteChat:user";
const SUBTITLE_TEXT =
  "To start chatting with Delta V about everything BankX, enter your first name and email.";

export const Chat: FunctionComponent = () => {
  const appRef = useRef<ReturnType<typeof createChat> | null>(null);
  const submitHandled = useRef<boolean>(false);

  useEffect(() => {
    submitHandled.current = false;

    const buildGreetings = (name?: string): string[] => [
      `Welcome to BankX${name ? `, ${name}` : ""}!`,
      "How can I help you?",
    ];
    const setSubtitle = (): boolean => {
      const el = document.querySelector(
        ".user-info-form-subtitle"
      ) as HTMLElement | null;
      if (el && el.textContent !== SUBTITLE_TEXT) {
        el.textContent = SUBTITLE_TEXT;

        return true;
      }

      return Boolean(el);
    };
    const retrySetSubtitle = (attempts = 20, delay = 200) => {
      let count = 0;

      const tick = () => {
        if (setSubtitle()) return;
        if (++count < attempts) setTimeout(tick, delay);
      };

      tick();
    };

    let stored: {
      email: string;
      name: string;
    } | null = null;

    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch {}

    const hasUser = Boolean(stored?.name && stored?.email);
    const storedSessionId = (() => {
      try {
        return localStorage.getItem(SESSION_KEY);
      } catch {
        return null;
      }
    })();

    try {
      const app = createChat({
        chatInputKey: "chatInput",
        chatSessionKey: "sessionId",
        enableStreaming: true,
        i18n: {
          en: {
            closeButtonTooltip: "Close chat",
            footer: "",
            getStarted: "Start New Chat",
            inputPlaceholder: "Type your message…",
            subtitle: "The AI clone of Lance Parker, founder of BankX.",
            title: "Delta V",
          },
        },
        initialMessages: hasUser ? buildGreetings(stored?.name) : [],
        loadPreviousSession: true,
        metadata: {
          appName: APP_NAME,
          platform: PLATFORM,
          source: "website",
          version: APP_VERSION,
          ...(hasUser && stored
            ? {
                email: stored.email,
                name: stored.name,
              }
            : {}),
        },
        mode: "window",
        showPoweredBy: false,
        showWelcomeScreen: !hasUser,
        target: "#chat",
        webhookUrl: WEBHOOK_URL,
        welcomeComponent: CollectUser,
        welcomePersonalGreeting: false,
      });

      appRef.current = app;

      retrySetSubtitle();
      setTimeout(() => {
        const chat = (app as VueAppConfig)?.config?.globalProperties?.$chat;

        if (!chat) return;

        if (
          storedSessionId &&
          chat.currentSessionId?.value == null &&
          chat.currentSessionId
        ) {
          chat.currentSessionId.value = storedSessionId;
        }

        const origStart = chat.startNewSession;

        chat.startNewSession = async (...args: unknown[]) => {
          const res = await origStart?.(...args);
          const sid = chat.currentSessionId?.value;

          if (sid) {
            try {
              localStorage.setItem(SESSION_KEY, sid);
            } catch {}
          }

          return res;
        };
      }, 0);

      const onWelcomeSubmit = ({ target }: Event) => {
        if (submitHandled.current) return;

        const form = target as HTMLFormElement | null;

        if (!form?.matches("form.collect-user, form.user-info-form")) return;

        const nameVal =
          form
            .querySelector<HTMLInputElement>('input[name="name"]')
            ?.value?.trim() || "";
        const emailVal =
          form
            .querySelector<HTMLInputElement>('input[name="email"]')
            ?.value?.trim() || "";

        if (!nameVal || !emailVal) return;

        submitHandled.current = true;

        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ name: nameVal, email: emailVal })
          );
        } catch {}

        try {
          const options = (app as VueAppContext)?._context?.provides
            ?.ChatOptions;
          if (options) {
            options.initialMessages = buildGreetings(nameVal);
          }
        } catch {}
      };

      document
        .getElementById("chat")
        ?.addEventListener("submit", onWelcomeSubmit, true);
      document.addEventListener("submit", onWelcomeSubmit, true);

      (appRef.current as AppRefWithCleanup).__cleanup = () => {
        document
          .getElementById("chat")
          ?.removeEventListener("submit", onWelcomeSubmit, true);
        document.removeEventListener("submit", onWelcomeSubmit, true);
      };
    } catch (e) {
      console.error("[BankXChat] Failed to initialize chat", e);
    }

    return () => {
      try {
        (appRef.current as AppRefWithCleanup)?.__cleanup?.();
      } catch {}
      try {
        appRef.current?.unmount?.();
      } catch {}
    };
  }, []);

  return <div id="chat" />;
};
